import { useEffect, useMemo, useRef, useState } from "react";

import { CorrelationCheck } from "./CorrelationCheck.js";
import { GAMES, SPORTS, initialSelections } from "./demoData.js";
import {
  loadEspnGames,
  loadEspnRoster,
  pickGamePatch,
  playerPatch,
  rosterEntries,
  rosterIndex,
  todayIso,
  unlockPatch,
} from "./espn.js";
import { AffiliateFooterLink, PartnerModule } from "./PartnerSlots.js";
import {
  clientFallbackAnalysis,
  devigAmericanOdds,
  estimatedPriceCost,
  formatAmericanOdds,
  gameSideOptions,
  hitsIn100,
  isValidOwnSlipLeg,
  legSideKey,
  normalizeLegTag,
  ownMarketFields,
  ownSlipLegLabel,
  ownSlipPricingLeg,
  parlayAnalysis,
  parseAmericanOdds,
  pricingLeg,
  SIDE_KEYS,
  SIDE_LABELS,
  sidePatch,
} from "./pricing.js";
import { routeForPathname } from "./routes.js";
import { downloadAndShareCard } from "./shareCard.js";

const DAYS = [
  { id: "yesterday", label: "Yesterday" },
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
];

const OWN_SLIP_SPORTS = ["baseball", "basketball", "hockey", "football", "soccer"];
const OWN_MARKETS = [
  { id: "moneyline", label: "Moneyline" },
  { id: "spread", label: "Spread / run line" },
  { id: "total", label: "Game total" },
  { id: "team_total", label: "Team total" },
  { id: "player_prop", label: "Player prop" },
];

const SPORTSBOOKS = [
  { id: "fanduel", label: "FanDuel" },
  { id: "draftkings", label: "DraftKings" },
  { id: "betmgm", label: "BetMGM" },
  { id: "caesars", label: "Caesars" },
  { id: "bet365", label: "bet365" },
  { id: "espnbet", label: "ESPN BET" },
  { id: "fanatics", label: "Fanatics" },
  { id: "hardrock", label: "Hard Rock Bet" },
  { id: "pinnacle", label: "Pinnacle" },
  { id: "bovada", label: "Bovada" },
  { id: "betonline", label: "BetOnline" },
  { id: "other", label: "Other" },
];

function blankOwnLeg(id) {
  return {
    id,
    marketType: "moneyline",
    // Which of the group's two sides this leg took — never the team text itself.
    side: "",
    teamLock: null,
    player: "",
    playerId: "",
    direction: "over",
    line: "",
    americanOdds: "",
    oppositeOdds: "",
  };
}

function blankOwnGame(id, legId) {
  return {
    id,
    sport: "baseball",
    date: todayIso(),
    espnGameId: "",
    teams: [],
    // A game has exactly two sides. They are named here once, and every leg picks one of them.
    sides: { a: "", b: "" },
    sidesLocked: false,
    legs: [blankOwnLeg(legId)],
  };
}

function ownSlipGroups(games) {
  return games.map((game, index) => ({
    game: { id: game.id, sport: game.sport, label: `Game ${index + 1}` },
    legs: game.legs.map((leg) => {
      const requestLeg = ownSlipPricingLeg(game, leg);
      if (!requestLeg) return null;
      return {
        ...requestLeg,
        label: ownSlipLegLabel(game, leg),
        odds: requestLeg.americanOdds,
        marginalProbability: devigAmericanOdds(leg.americanOdds, leg.oppositeOdds),
      };
    }).filter(Boolean),
  })).filter((group) => group.legs.length > 0);
}

function gameLabel(game) {
  return game.label ?? `${game.away.name} at ${game.home.name}`;
}

function plainVoidReason(reason) {
  const text = String(reason ?? "");
  if (text.includes("contradictory legs")) {
    return "These picks cannot both win because they use the same total and line in opposite directions.";
  }
  if (text.includes("mutually exclusive legs")) {
    return "These picks cannot both win: no final score in this game satisfies all of them.";
  }
  return reason ?? "The selected legs contradict each other.";
}

function selectedParlayGroups(selections) {
  return GAMES.map((game) => {
    const selectedIds = selections[game.id] ?? [];
    const legs = game.markets
      .flatMap((marketGroup) => marketGroup.options.map((option) => ({
        ...option,
        marketId: marketGroup.id,
      })))
      .filter((option) => selectedIds.includes(option.id));
    return { game, legs };
  }).filter((group) => group.legs.length > 0);
}

function scrollToSection(id, closeMenu) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  closeMenu?.();
}

function Brand() {
  return (
    <a className="brand" href="/" aria-label="PricedPlay home">
      <img src="/pricedplay-brand.png" alt="" />
      <span>PricedPlay</span>
    </a>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10v6M12 7h.01" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 2.6 2.6L16.5 9" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function ChevronIcon({ direction = "right" }) {
  return (
    <svg className={`chevron chevron--${direction}`} viewBox="0 0 24 24" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function SportIcon({ sport }) {
  const icon = {
    all: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    baseball: <><circle cx="12" cy="12" r="8" /><path d="M8 5.2c1.7 1.9 1.7 4 0 6M16 12.8c-1.7 1.9-1.7 4 0 6" /></>,
    basketball: <><circle cx="12" cy="12" r="8" /><path d="M4 12h16M12 4c3 3 3 13 0 16M12 4c-3 3-3 13 0 16" /></>,
    hockey: <><path d="M5 15c2.4 3.2 10.8 4 14-.5l-1.5-7.8C13 9 9.5 9.5 5 8.5Z" /><path d="M6 12c3 1.5 8 1.6 12-.5" /></>,
    football: <><path d="M5 15c2.5 4 8.8 5.2 13 1 4.2-4.2 3-10.5-1-13-4.8-.3-8 1-10.3 3.3C4.4 8.6 4.1 11.8 5 15Z" /><path d="m8 16 8-8m-5 3 2 2m0-5 3 3" /></>,
    soccer: <><circle cx="12" cy="12" r="8" /><path d="m12 8 3 2-1 4h-4l-1-4Zm-3-4 3 4m4-2-1 4m5 3-6 1m-2 6-2-6m-6-1 6 1" /></>,
  }[sport];

  return <svg className="sport-icon" viewBox="0 0 24 24" aria-hidden="true">{icon}</svg>;
}

function Header({ onNavigatePlans }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  const handleBuilder = () => {
    close();
    scrollToSection("parlay-check");
  };

  return (
    <header className="site-header">
      <Brand />
      <button
        className="menu-button"
        type="button"
        aria-label="Toggle navigation"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((value) => !value)}
      >
        <span />
        <span />
        <span />
      </button>
      <nav className={menuOpen ? "nav-links nav-links--open" : "nav-links"} aria-label="Primary navigation">
        <button className="nav-link--active" type="button" onClick={handleBuilder}>Parlay Check</button>
        <button type="button" onClick={() => scrollToSection("scores", close)}>Scores</button>
        <button type="button" onClick={() => scrollToSection("how-it-works", close)}>How it works</button>
        <button type="button" onClick={() => { close(); onNavigatePlans(); }}>Plans</button>
      </nav>
    </header>
  );
}

function DateControls({ selectedDay, onChange }) {
  return (
    <div className="date-controls" aria-label="Score date">
      <button
        className="date-arrow"
        type="button"
        aria-label="Previous day"
        onClick={() => onChange(selectedDay === "tomorrow" ? "today" : "yesterday")}
      >
        <ChevronIcon direction="left" />
      </button>
      {DAYS.map((day) => (
        <button
          className={selectedDay === day.id ? "date-button date-button--selected" : "date-button"}
          type="button"
          aria-pressed={selectedDay === day.id}
          key={day.id}
          onClick={() => onChange(day.id)}
        >
          {day.id === "today" && <span className="calendar-icon" aria-hidden="true" />}
          {day.label}
        </button>
      ))}
      <button
        className="date-arrow"
        type="button"
        aria-label="Next day"
        onClick={() => onChange(selectedDay === "yesterday" ? "today" : "tomorrow")}
      >
        <ChevronIcon />
      </button>
    </div>
  );
}

function SportFilters({ selectedSport, onChange }) {
  return (
    <div className="sport-filter-wrap">
      <div className="sport-filters" aria-label="Filter by sport">
        {SPORTS.map((sport) => (
          <button
            className={selectedSport === sport.id ? "sport-filter sport-filter--selected" : "sport-filter"}
            type="button"
            aria-pressed={selectedSport === sport.id}
            key={sport.id}
            onClick={() => onChange(sport.id)}
          >
            <SportIcon sport={sport.id} />
            {sport.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function GameRow({ game, selected, onSelect }) {
  const hasScore = game.away.score !== undefined;

  return (
    <button
      className={selected ? `game-row game-row--${game.phase} game-row--selected` : `game-row game-row--${game.phase}`}
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(game.id)}
    >
      <span className="game-mark">{game.away.abbreviation}</span>
      <span className="game-teams">
        <strong>{game.away.name}</strong>
        <span>at</span>
        <strong>{game.home.name}</strong>
      </span>
      {game.phase === "live" && (
        <span className="game-status-cell">
          <span className="game-state"><strong>{game.state}</strong><small>{game.detail}</small></span>
        </span>
      )}
      {game.phase === "upcoming" && <span className="game-time"><small>{game.day === "today" ? "Today" : "Tomorrow"}</small>{game.time}</span>}
      {game.phase === "final" && <span className="game-status-cell"><span className="final-chip">Final</span></span>}
      {hasScore && <span className="game-score"><strong>{game.away.score}</strong><strong>{game.home.score}</strong></span>}
      <span className="game-league"><SportIcon sport={game.sport} /><span><strong>{SPORTS.find((sport) => sport.id === game.sport)?.label}</strong><small>{game.league}</small></span></span>
      <ChevronIcon />
    </button>
  );
}

function ScoreSection({ title, tone, games, selectedGameId, onSelect }) {
  if (games.length === 0) return null;

  return (
    <section className={`score-section score-section--${tone}`} aria-labelledby={`${tone}-heading`}>
      <div className="score-section-heading">
        <h3 id={`${tone}-heading`}><span />{title}</h3>
        <div className="column-labels" aria-hidden="true">
          <span>{tone === "upcoming" ? "Time" : "Status"}</span>
          <span>{tone === "upcoming" ? "League" : "Score"}</span>
          {tone !== "upcoming" && <span>League</span>}
        </div>
      </div>
      <div className="game-list">
        {games.map((game) => (
          <GameRow
            game={game}
            selected={game.id === selectedGameId}
            onSelect={onSelect}
            key={game.id}
          />
        ))}
      </div>
    </section>
  );
}

function ScoresWorkspace({ selectedGameId, onSelectGame }) {
  const [selectedDay, setSelectedDay] = useState("today");
  const [selectedSport, setSelectedSport] = useState("all");

  const visibleGames = useMemo(
    () => GAMES.filter((game) => game.day === selectedDay && (selectedSport === "all" || game.sport === selectedSport)),
    [selectedDay, selectedSport],
  );
  const liveGames = visibleGames.filter((game) => game.phase === "live");
  const upcomingGames = visibleGames.filter((game) => game.phase === "upcoming");
  const finalGames = visibleGames.filter((game) => game.phase === "final");

  useEffect(() => {
    if (visibleGames.length > 0 && !visibleGames.some((game) => game.id === selectedGameId)) {
      onSelectGame(visibleGames[0].id);
    }
  }, [onSelectGame, selectedGameId, visibleGames]);

  return (
    <section className="scores-workspace" id="scores" aria-labelledby="scores-title">
      <div className="scores-title-row">
        <div>
          <h2 id="scores-title">Scores</h2>
          <p className="score-provenance">Sample slate - live scores coming soon.</p>
        </div>
      </div>
      <DateControls selectedDay={selectedDay} onChange={setSelectedDay} />
      <SportFilters selectedSport={selectedSport} onChange={setSelectedSport} />
      <ScoreSection title="In progress" tone="live" games={liveGames} selectedGameId={selectedGameId} onSelect={onSelectGame} />
      <ScoreSection title="Upcoming" tone="upcoming" games={upcomingGames} selectedGameId={selectedGameId} onSelect={onSelectGame} />
      <ScoreSection title="Final scores" tone="final" games={finalGames} selectedGameId={selectedGameId} onSelect={onSelectGame} />
      {visibleGames.length === 0 && (
        <div className="empty-state">
          <strong>No games in this view</strong>
          <span>Choose All or another sport to see the slate.</span>
        </div>
      )}
    </section>
  );
}

function MarketGroup({ gameId, market, selectedIds, onToggle }) {
  return (
    <section className="market-group" aria-labelledby={`market-${market.id}`}>
      <div className="market-title">
        <h3 id={`market-${market.id}`}>{market.label}</h3>
        <ChevronIcon direction="up" />
      </div>
      <div className="market-options">
        {market.options.map((option) => {
          const selected = selectedIds.includes(option.id);
          return (
            <button
              className={selected ? "market-option market-option--selected" : "market-option"}
              type="button"
              aria-pressed={selected}
              key={option.id}
              onClick={() => onToggle(gameId, market, option.id)}
            >
              <span>{option.label}</span>
              <strong>{formatAmericanOdds(option.odds)}</strong>
              <i>{selected ? <CheckIcon /> : "+"}</i>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SportsbookSelector({ bookId, otherBookName, onBookChange, onOtherBookNameChange }) {
  return (
    <section className="book-selector" aria-labelledby="sportsbook-heading">
      <label>
        <span id="sportsbook-heading">Which sportsbook is this slip from? <small>required</small></span>
        <select required value={bookId} onChange={(event) => onBookChange(event.target.value)}>
          <option value="">Select a sportsbook</option>
          {SPORTSBOOKS.map((book) => <option value={book.id} key={book.id}>{book.label}</option>)}
        </select>
      </label>
      {bookId === "other" && (
        <label>
          <span>Sportsbook name <small>optional</small></span>
          <input
            value={otherBookName}
            maxLength="40"
            placeholder="Sportsbook name"
            onChange={(event) => onOtherBookNameChange(event.target.value.slice(0, 40))}
          />
        </label>
      )}
      <p>Used anonymously to measure how books price parlays. <a href="/privacy">See Privacy.</a></p>
    </section>
  );
}

// The slate is a convenience, never a dependency: any failure resolves to an empty list with a
// reason, and the builder stays fully usable by typing names.
function useEspnSlate(sport, date) {
  const [slate, setSlate] = useState({ status: "idle", games: [], reason: null });
  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setSlate({ status: "loading", games: [], reason: null });
    loadEspnGames(sport, date, { signal: controller.signal })
      .then((result) => { if (live) setSlate({ status: "ready", ...result }); })
      .catch(() => { if (live) setSlate({ status: "ready", games: [], reason: null }); });
    return () => { live = false; controller.abort(); };
  }, [sport, date]);
  return slate;
}

function useEspnRoster(sport, teams) {
  const teamKey = teams.map((team) => team.id || team.abbrev).join("|");
  const [roster, setRoster] = useState({ status: "idle", players: [], index: new Map() });
  useEffect(() => {
    if (!teamKey) {
      setRoster({ status: "idle", players: [], index: new Map() });
      return undefined;
    }
    const controller = new AbortController();
    let live = true;
    setRoster({ status: "loading", players: [], index: new Map() });
    Promise.all(teams.map(async (team) => ({
      team,
      players: (await loadEspnRoster(sport, team.id || team.abbrev, { signal: controller.signal })).players,
    })))
      .then((rosters) => {
        if (!live) return;
        const players = rosterEntries(rosters);
        setRoster({ status: "ready", players, index: rosterIndex(players) });
      })
      .catch(() => { if (live) setRoster({ status: "ready", players: [], index: new Map() }); });
    return () => { live = false; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport, teamKey]);
  return roster;
}

// The leg's team control is a SELECT over this game's two sides and nothing else. There is no
// free-text box here, so the second leg on a team cannot spell it a third way and be read as the
// opponent — the failure this control exists to make unrepresentable.
function SideField({ game, leg, required, onPatch }) {
  const locked = Boolean(leg.teamLock);
  const options = gameSideOptions(game);
  const selected = legSideKey(game, leg);
  const lockHint = locked
    ? `Side set from the ${leg.teamLock.teamName} roster${leg.teamLock.position ? ` · ${leg.teamLock.position}` : ""}. Unlock to choose the other side.`
    : undefined;

  return (
    <label className={locked ? "own-team-field own-team-field--locked" : "own-team-field"} title={lockHint}>
      <span>
        Team <small>{required ? "required" : "optional"}</small>
        {locked && <button className="team-unlock" type="button" onClick={() => onPatch(unlockPatch())}>Unlock</button>}
      </span>
      <select
        value={selected}
        disabled={locked || options.length === 0}
        aria-describedby={locked ? `${leg.id}-lock` : undefined}
        onChange={(event) => onPatch(sidePatch(event.target.value))}
      >
        <option value="">{required ? "— team —" : "n/a"}</option>
        {options.map((side) => <option value={side.key} key={side.key}>{side.name}</option>)}
      </select>
      {locked && <small className="field-hint" id={`${leg.id}-lock`}>Set from the roster</small>}
      {options.length === 0 && <small className="field-hint">Name this game&apos;s teams first</small>}
    </label>
  );
}

// The two sides of one game, named once for every leg in the group.
function GameSidesFields({ game, onGameChange }) {
  return (
    <div className="own-game-sides">
      {SIDE_KEYS.map((key) => (
        <label key={key}>
          <span>{SIDE_LABELS[key]} <small>{game.sidesLocked ? "from the picked game" : "name it once"}</small></span>
          <input
            value={game.sides?.[key] ?? ""}
            maxLength="40"
            disabled={game.sidesLocked}
            placeholder={key === "a" ? "Yankees" : "Red Sox"}
            aria-label={`${SIDE_LABELS[key]} for game ${game.id}`}
            onChange={(event) => onGameChange(game.id, { sides: { ...game.sides, [key]: event.target.value } })}
          />
        </label>
      ))}
    </div>
  );
}

function OwnLegFields({ game, leg, legIndex, gameIndex, canRemove, roster, rosterListId, onPatch, onRemove }) {
  const fields = ownMarketFields(leg.marketType);
  const oddsValid = parseAmericanOdds(leg.americanOdds) !== null;
  const oppositeValid = !String(leg.oppositeOdds).trim() || parseAmericanOdds(leg.oppositeOdds) !== null;
  const requires = (field) => fields.required.includes(field);

  const changeMarket = (marketType) => {
    const next = ownMarketFields(marketType);
    onPatch(next.player
      ? { marketType }
      : { marketType, player: "", playerId: "", teamLock: null });
  };

  // A roster hit fixes the player's identity AND which side he is on, so two props on one player
  // share a tag and the side is never mistyped. The lock is visible and reversible.
  const changePlayer = (value) => onPatch(playerPatch(roster.index, value, game));

  return (
    <div className="own-leg">
      <div className="own-leg-heading">
        <strong>Leg {legIndex + 1}</strong>
        <span className="own-leg-summary">{ownSlipLegLabel(game, leg)}</span>
        {canRemove && <button type="button" aria-label={`Remove leg ${legIndex + 1} from game ${gameIndex + 1}`} onClick={onRemove}><CloseIcon /></button>}
      </div>
      <div className="own-leg-grid">
        <label>
          <span>Market</span>
          <select value={leg.marketType} onChange={(event) => changeMarket(event.target.value)}>
            {OWN_MARKETS.map((market) => <option value={market.id} key={market.id}>{market.label}</option>)}
          </select>
        </label>
        {fields.player && (
          <label>
            <span>Player <small>{requires("player") ? "required" : "optional"}</small></span>
            <input
              value={leg.player}
              maxLength="60"
              list={rosterListId}
              placeholder="J. Alvarez"
              aria-invalid={leg.player.length > 0 && !normalizeLegTag(leg.player)}
              onChange={(event) => changePlayer(event.target.value)}
            />
          </label>
        )}
        {fields.team && <SideField game={game} leg={leg} required={requires("team")} onPatch={onPatch} />}
        {fields.direction && (
          <label>
            <span>Direction</span>
            <select value={leg.direction} onChange={(event) => onPatch({ direction: event.target.value })}>
              <option value="over">Over</option>
              <option value="under">Under</option>
            </select>
          </label>
        )}
        {fields.line && (
          <label>
            <span>Line <small>{requires("line") ? "required" : "optional"}</small></span>
            <input
              value={leg.line}
              inputMode="decimal"
              placeholder="-1.5"
              aria-invalid={Boolean(leg.line) && !Number.isFinite(Number(leg.line))}
              onChange={(event) => onPatch({ line: event.target.value })}
            />
          </label>
        )}
        <label>
          <span>Leg odds <small>required</small></span>
          <input
            value={leg.americanOdds}
            inputMode="text"
            placeholder="-110"
            aria-invalid={leg.americanOdds.length > 0 && !oddsValid}
            onChange={(event) => onPatch({ americanOdds: event.target.value })}
          />
        </label>
        <label>
          <span>Opposite odds <small>optional</small></span>
          <input
            value={leg.oppositeOdds}
            inputMode="text"
            placeholder="-110"
            aria-invalid={!oppositeValid}
            onChange={(event) => onPatch({ oppositeOdds: event.target.value })}
          />
          <small className="field-hint">Improves accuracy</small>
        </label>
      </div>
    </div>
  );
}

function OwnGameFieldset({ game, gameIndex, gameCount, totalLegs, onGameChange, onLegPatch, onAddLeg, onRemoveLeg, onRemoveGame }) {
  const slate = useEspnSlate(game.sport, game.date);
  const roster = useEspnRoster(game.sport, game.teams);
  const rosterListId = `roster-${game.id}`;
  const slateNote = slate.status === "loading"
    ? "Loading games…"
    : slate.reason ?? (slate.games.length === 0 ? "No games listed for that date — enter names manually." : null);

  // Picking a listed game fills and locks both sides; every leg then picks one of exactly those two.
  const pickGame = (espnGameId) => {
    const chosen = slate.games.find((item) => item.id === espnGameId);
    onGameChange(game.id, pickGamePatch(game, chosen ?? null));
  };

  return (
    <fieldset className="own-game">
      <legend>Game {gameIndex + 1}</legend>
      <div className="own-game-toolbar">
        <label>
          <span>Sport</span>
          <select value={game.sport} onChange={(event) => onGameChange(game.id, { sport: event.target.value, espnGameId: "", teams: [], sidesLocked: false })}>
            {OWN_SLIP_SPORTS.map((sport) => <option value={sport} key={sport}>{sport[0].toUpperCase() + sport.slice(1)}</option>)}
          </select>
        </label>
        <label>
          <span>Date</span>
          <input type="date" value={game.date} onChange={(event) => onGameChange(game.id, { date: event.target.value, espnGameId: "", teams: [], sidesLocked: false })} />
        </label>
        <label className="own-game-picker">
          <span>Game <small>optional</small></span>
          <select value={game.espnGameId} onChange={(event) => pickGame(event.target.value)}>
            <option value="">Not listed — enter names manually</option>
            {slate.games.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
          </select>
        </label>
        {gameCount > 1 && <button type="button" onClick={() => onRemoveGame(game.id)}>Remove game</button>}
      </div>
      <GameSidesFields game={game} onGameChange={onGameChange} />
      {slateNote && <p className="own-game-note">{slateNote}</p>}
      <datalist id={rosterListId}>
        {roster.players.map((player) => (
          <option value={player.name} key={`${player.team}-${player.playerId || player.name}`}>
            {player.position ? `${player.position} · ${player.team}` : player.team}
          </option>
        ))}
      </datalist>
      {game.legs.map((leg, legIndex) => (
        <OwnLegFields
          key={leg.id}
          game={game}
          leg={leg}
          legIndex={legIndex}
          gameIndex={gameIndex}
          canRemove={game.legs.length > 1}
          roster={roster}
          rosterListId={rosterListId}
          onPatch={(patch) => onLegPatch(game.id, leg.id, patch)}
          onRemove={() => onRemoveLeg(game.id, leg.id)}
        />
      ))}
      <button className="add-leg-button" type="button" disabled={totalLegs >= 12} onClick={() => onAddLeg(game.id)}>+ Add leg to this game</button>
    </fieldset>
  );
}

function OwnSlipEditor({ games, onGameChange, onLegPatch, onAddLeg, onRemoveLeg, onAddGame, onRemoveGame }) {
  const totalLegs = games.reduce((count, game) => count + game.legs.length, 0);

  return (
    <section className="builder-step own-slip-editor" aria-labelledby="own-slip-heading">
      <div className="builder-step-title">
        <h3 id="own-slip-heading">Your parlay</h3>
        <span>{totalLegs} {totalLegs === 1 ? "leg" : "legs"} · {games.length} {games.length === 1 ? "game" : "games"}</span>
      </div>
      <p className="own-slip-intro">Group picks from the same event together so the model can estimate their correlation. Each game has two teams: pick the game to fill them in, or type them once — then every leg chooses one of those two, so legs on the same team always match. American odds must be +100 or longer, or -100 or shorter.</p>
      {games.map((game, gameIndex) => (
        <OwnGameFieldset
          key={game.id}
          game={game}
          gameIndex={gameIndex}
          gameCount={games.length}
          totalLegs={totalLegs}
          onGameChange={onGameChange}
          onLegPatch={onLegPatch}
          onAddLeg={onAddLeg}
          onRemoveLeg={onRemoveLeg}
          onRemoveGame={onRemoveGame}
        />
      ))}
      <button className="add-game-button" type="button" disabled={totalLegs >= 12} onClick={onAddGame}>+ Add another game</button>
    </section>
  );
}

function SGPBuilder({ game, groups, pricingState, selections, offeredOdds, mode, canCompare, ownGames, bookId, otherBookName, partnerSlots, showShare, shareStatus, onModeChange, onBookChange, onOtherBookNameChange, onOwnGameChange, onOwnLegPatch, onAddOwnLeg, onRemoveOwnLeg, onRemoveOwnGame, onOddsChange, onToggle, onClear, onCompare, onShare, onAddGame }) {
  const selectedIds = selections[game.id] ?? [];
  const totalLegs = groups.reduce((count, group) => count + group.legs.length, 0);
  const analysis = pricingState.analysis;
  const offered = parseAmericanOdds(offeredOdds);
  const priceCost = analysis && !analysis.void
    ? estimatedPriceCost(analysis.fairProbability, offeredOdds)
    : null;
  const correlationGroups = groups.filter((group) => group.legs.length > 1);
  const priceCostPercent = priceCost === null ? null : Math.round(Math.abs(priceCost) * 100);
  const result = totalLegs === 0
    ? {
        label: "Add legs and a valid total price",
        detail: "Enter the combined American odds shown by your sportsbook.",
        tone: "neutral",
      }
    : pricingState.status === "loading"
      ? {
          label: "Calculating the correlation-adjusted price",
          detail: "The pricing model is checking the selected legs.",
          tone: "neutral",
        }
      : analysis?.void
        ? {
            label: "This combination cannot be priced",
            detail: plainVoidReason(analysis.voidReason),
            tone: "negative",
          }
        : priceCost === null
          ? {
            label: "Add a valid total price",
            detail: "Enter the combined American odds shown by your sportsbook.",
            tone: "neutral",
          }
          : priceCost > 0.01
            ? {
                label: `About ${priceCostPercent}% estimated price cost`,
                detail: "The sportsbook payout is lower than our no-vig estimate.",
                tone: "negative",
              }
            : priceCost < -0.01
              ? {
                  label: "Sportsbook price is above fair estimate",
                  detail: "The entered payout is higher than our no-vig estimate.",
                  tone: "positive",
                }
              : {
                  label: "Price is close to fair estimate",
                  detail: "The entered payout is close to our no-vig estimate.",
                  tone: "neutral",
                };
  const fairPrice = pricingState.status === "loading"
    ? "Calculating…"
    : analysis?.void
      ? "Void"
      : analysis?.fairOdds != null
        ? formatAmericanOdds(analysis.fairOdds)
        : "—";
  const fixtureState = game.phase === "live" ? `${game.state} · ${game.detail}` : `${game.day === "today" ? "Today" : "Tomorrow"} · ${game.time}`;

  return (
    <aside className="sgp-builder" id="sgp-builder" aria-label="Parlay price checker">
      <div className="builder-header">
        <div>
          <div className="builder-title"><h2>Parlay Check</h2><InfoIcon /></div>
          <p>Recreate the legs, then enter the sportsbook&apos;s total price.</p>
        </div>
      </div>

      <div className="builder-mode-toggle" role="group" aria-label="Parlay Check mode">
        <button className={mode === "own" ? "builder-mode--active" : ""} type="button" aria-pressed={mode === "own"} onClick={() => onModeChange("own")}>Your own slip</button>
        <button className={mode === "demo" ? "builder-mode--active" : ""} type="button" aria-pressed={mode === "demo"} onClick={() => onModeChange("demo")}>Sample slate</button>
      </div>

      {mode === "demo" ? <>
        <section className="builder-step builder-markets" aria-labelledby="add-picks-heading">
        <div className="active-game-title">
          <div>
            <h3 id="add-picks-heading">{game.away.name} at {game.home.name}</h3>
            <span>{fixtureState}</span>
          </div>
          <strong>Add legs</strong>
        </div>
          {game.markets.map((market) => (
          <MarketGroup gameId={game.id} market={market} selectedIds={selectedIds} onToggle={onToggle} key={market.id} />
        ))}
        </section>

        <section className="builder-step selected-legs parlay-slip" aria-labelledby="selected-legs-heading">
        <div className="builder-step-title">
          <h3 id="selected-legs-heading">Your parlay</h3>
          <span>{totalLegs} {totalLegs === 1 ? "leg" : "legs"} · {groups.length} {groups.length === 1 ? "game" : "games"}</span>
        </div>
        {groups.length === 0 ? (
          <p className="builder-empty">Choose a game, then recreate the legs from your sportsbook slip.</p>
        ) : groups.map((group, groupIndex) => (
          <div className="parlay-game" key={group.game.id}>
            <div className="parlay-game-title">
              <span>{groupIndex + 1}</span>
              <strong>{group.game.away.name} at {group.game.home.name}</strong>
              <small>{group.legs.length} {group.legs.length === 1 ? "leg" : "legs"}</small>
            </div>
            {group.legs.map((leg) => (
              <div className="selected-leg" key={`${group.game.id}-${leg.id}`}>
                <CheckIcon />
                <strong>{leg.label}</strong>
                <b>{formatAmericanOdds(leg.odds)}</b>
                <button type="button" aria-label={`Remove ${leg.label}`} onClick={() => onToggle(group.game.id, null, leg.id)}><CloseIcon /></button>
              </div>
            ))}
          </div>
        ))}
        <div className="parlay-slip-footer">
          <span>{totalLegs} {totalLegs === 1 ? "leg" : "legs"} · {groups.length} {groups.length === 1 ? "game" : "games"}</span>
          <button type="button" onClick={onAddGame}>+ Add another game</button>
        </div>
        </section>
      </> : (
        <>
          <SportsbookSelector
            bookId={bookId}
            otherBookName={otherBookName}
            onBookChange={onBookChange}
            onOtherBookNameChange={onOtherBookNameChange}
          />
          <OwnSlipEditor
            games={ownGames}
            onGameChange={onOwnGameChange}
            onLegPatch={onOwnLegPatch}
            onAddLeg={onAddOwnLeg}
            onRemoveLeg={onRemoveOwnLeg}
            onAddGame={onAddGame}
            onRemoveGame={onRemoveOwnGame}
          />
        </>
      )}

      <section className="builder-summary" aria-label="Price comparison">
        <label className="offered-price">
          <span>Sportsbook total price <InfoIcon /></span>
          <input value={offeredOdds} inputMode="text" onChange={(event) => onOddsChange(event.target.value)} aria-label="Sportsbook total American odds" />
        </label>
        <div className="summary-row">
          <span>
            No-vig fair price <InfoIcon />
            {pricingState.status === "fallback" && <small>approximate (correlation model unavailable)</small>}
          </span>
          <strong>{fairPrice}</strong>
        </div>
        <div className="summary-row">
          <span>Our estimate <InfoIcon /></span>
          <strong>{analysis && !analysis.void ? `${hitsIn100(analysis.fairProbability)} hits in 100` : "—"}</strong>
        </div>
        <div className="summary-row">
          <span>Sportsbook price needs <InfoIcon /></span>
          <strong>{offered ? `${hitsIn100(offered.probability)} hits in 100` : "—"}</strong>
        </div>
        <div className={`builder-verdict builder-verdict--${result.tone}`} aria-live="polite">
          <InfoIcon />
          <div><strong>{result.label}</strong><span>{result.detail}</span></div>
        </div>
        {/* Anything the model had to read into the slip is said out loud rather than folded into
            the price — a misread team name warns here instead of quietly reporting 0%. */}
        {(analysis?.warnings ?? []).map((warning) => (
          <p className="builder-warning" aria-live="polite" key={`${warning.gameId}-${warning.code}-${warning.legs.join("-")}`}>
            <InfoIcon />
            <span>{warning.message}</span>
          </p>
        ))}
        <p className="pricing-note">{mode === "own" ? "Leg probabilities come from de-vigging the odds you entered. Correlation comes from the PricedPlay pricing model. Estimates, not guarantees." : "Correlation-adjusted by the PricedPlay pricing model. The sample slate uses fictional data."}</p>
      </section>

      {mode === "own" && <CorrelationCheck correlationCheck={analysis?.correlationCheck} />}

      {showShare && (
        <section className="share-check" aria-label="Share this check">
          <button className="secondary-button" type="button" onClick={onShare}>Share this check</button>
          <p aria-live="polite">{shareStatus}</p>
        </section>
      )}

      <PartnerModule slots={partnerSlots} />

      {correlationGroups.length > 0 && (
        <details className="correlation-details" open>
          <summary>Explain the correlation <ChevronIcon direction="up" /></summary>
          {pricingState.status === "loading" ? (
            <p>Calculating the correlation effect for this game.</p>
          ) : pricingState.status === "fallback" ? (
            <p>Correlation adjustment unavailable. The approximate price multiplies the independent leg probabilities only.</p>
          ) : correlationGroups.map((group) => {
              const pricedGame = analysis?.perGame.find((item) => item.gameId === group.game.id);
              if (!pricedGame) return null;
              const points = pricedGame.correlationDelta * 100;
              const signedPoints = `${points > 0 ? "+" : ""}${points.toFixed(1)} points`;
              const legLabels = new Map(group.legs.map((leg) => [leg.id, leg.label || leg.id]));
              const describe = (id) => legLabels.get(id) ?? id;
              return (
                <div className="correlation-game" key={group.game.id}>
                  <strong>{gameLabel(group.game)}</strong>
                  <div>
                    <span>Legs independently<b>{(pricedGame.independentProbability * 100).toFixed(1)}%</b></span>
                    <span>Correlation effect<b>{signedPoints}</b></span>
                    <span>Model-adjusted<b>{(pricedGame.fairProbability * 100).toFixed(1)}%</b></span>
                  </div>
                  {(pricedGame.impliedCollapse ?? []).map((entry) => (
                    <p className="correlation-implied" key={entry.kept}>
                      {entry.kind === "nested"
                        ? `${entry.implied.map(describe).join(", ")} already happens whenever ${describe(entry.kept)} does, so these price as one outcome instead of two.`
                        : `${describe(entry.kept)} and ${entry.implied.map(describe).join(", ")} can only both land in a narrow band of final scores, so the model prices that overlap directly.`}
                    </p>
                  ))}
                  {(pricedGame.warnings ?? []).map((warning) => (
                    <p className="correlation-warning" key={`${warning.code}-${warning.legs.join("-")}`}>
                      {`${warning.legs.map(describe).join(" and ")}: ${warning.message}`}
                    </p>
                  ))}
                </div>
              );
            })}
          <p>{mode === "own" ? "Based only on the odds and grouping you entered. Pricing-model estimate, not a guarantee." : "Pricing-model estimate, not a guarantee. The sample slate uses fictional data."}</p>
        </details>
      )}

      <div className="builder-actions">
        <button className="secondary-button" type="button" onClick={onClear}>Clear parlay</button>
        <button className="primary-button" type="button" disabled={!canCompare} onClick={onCompare}>Compare price</button>
      </div>
    </aside>
  );
}

function HowItWorks() {
  const steps = [
    ["Choose your input", "Group the picks from your own slip by game, or explore the sample slate."],
    ["Enter the prices", "For your slip, add each leg's odds and the sportsbook's combined American price."],
    ["Compare all three views", "See the independent leg price, the model-adjusted price, and the total price implied by your book."],
  ];

  return (
    <section className="how-it-works" id="how-it-works" aria-label="How it works">
      {steps.map(([title, body], index) => (
        <article className="step" key={title}>
          <span className="step-number">{index + 1}</span>
          <div><h2>{title}</h2><p>{body}</p></div>
        </article>
      ))}
    </section>
  );
}

const ACCESS_PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    description: "Explore the sample slate without paying for analysis tools.",
    features: ["Sample in-progress, upcoming, and final scores", "Game status and schedule filters", "No sportsbook account connection"],
    action: "View scores",
  },
  {
    id: "edge",
    name: "Edge",
    price: "$12",
    cadence: "monthly target",
    description: "Understand the price behind your parlay before you decide what to do.",
    features: ["No-vig fair price", "Sportsbook price-cost estimate", "Same-game correlation breakdown"],
    action: "Preview Edge",
  },
  {
    id: "pro",
    name: "Pro API + MCP",
    price: "Contract",
    cadence: "future licensed access",
    description: "A future concept for approved PricedPlay analysis under written terms.",
    features: ["Planned API and MCP access", "Commercial usage terms", "Scope matched to licensed rights"],
    action: "Review future access",
  },
];

function AccessPlans({ onViewScores, onPreviewEdge }) {
  const [selectedPlan, setSelectedPlan] = useState("edge");
  const [message, setMessage] = useState("Edge tools are available to explore. Billing is not active.");

  const choosePlan = (planId) => {
    setSelectedPlan(planId);

    if (planId === "free") {
      setMessage("Free score access selected for this preview. No account or payment was created.");
      onViewScores();
      return;
    }

    if (planId === "edge") {
      setMessage("Edge tools are available to explore. Billing is not active.");
      onPreviewEdge();
      return;
    }

    setMessage("Pro access opens only after the data rights and commercial terms for each source are approved.");
    document.getElementById("usage-rules")?.setAttribute("open", "");
    window.setTimeout(() => scrollToSection("usage-rules"), 50);
  };

  return (
    <section className="access-plans" id="plans" aria-labelledby="plans-title">
      <div className="plans-heading">
        <div>
          <h2 id="plans-title">Scores stay free. Better pricing is the product.</h2>
          <p>This release uses a fictional scoreboard. Edge previews no-vig and correlation tools; Pro describes future access that would require written commercial terms.</p>
        </div>
        <span>PREVIEW RELEASE</span>
      </div>

      <div className="plan-grid">
        {ACCESS_PLANS.map((plan) => (
          <article className={selectedPlan === plan.id ? `plan plan--${plan.id} plan--selected` : `plan plan--${plan.id}`} key={plan.id}>
            <div className="plan-title">
              <h3>{plan.name}</h3>
              {plan.id === "edge" && <span>Core product</span>}
            </div>
            <div className="plan-price"><strong>{plan.price}</strong><span>{plan.cadence}</span></div>
            <p>{plan.description}</p>
            <ul>
              {plan.features.map((feature) => <li key={feature}><CheckIcon />{feature}</li>)}
            </ul>
            <button
              className={plan.id === "edge" ? "primary-button" : "secondary-button"}
              type="button"
              aria-pressed={selectedPlan === plan.id}
              onClick={() => choosePlan(plan.id)}
            >
              {plan.action}
            </button>
          </article>
        ))}
      </div>

      <p className="plan-message" aria-live="polite">{message}</p>

      <details className="usage-rules" id="usage-rules">
        <summary>How personal and commercial access would work <ChevronIcon direction="up" /></summary>
        <div>
          <p><strong>Free and Edge:</strong> personal analysis inside PricedPlay. They do not include bulk extraction, feed resale, or republishing our datasets.</p>
          <p><strong>Pro:</strong> a future API and MCP concept under a written commercial agreement. No commercial integration is active in this preview.</p>
          <p><strong>Hard boundary:</strong> PricedPlay will not expose or resell a third-party feed unless the source agreement expressly allows it. The preview takes no bets, holds no funds, and does not guarantee outcomes.</p>
        </div>
      </details>
    </section>
  );
}

export function SiteFooter({ partnerSlotsEnabled = false }) {
  return (
    <footer>
      <Brand />
      <span>(c) 2026 PricedPlay. Private product preview.</span>
      <nav aria-label="Legal">
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
        <AffiliateFooterLink enabled={partnerSlotsEnabled} />
      </nav>
    </footer>
  );
}

function PlansPage({ partnerSlotsEnabled, onNavigate }) {
  const backToPreview = (event) => {
    event.preventDefault();
    onNavigate("/");
  };

  return (
    <div className="app plans-shell">
      <header className="site-header legal-header">
        <Brand />
        <a href="/" onClick={backToPreview}>Back to preview</a>
      </header>
      <main className="plans-page">
        <AccessPlans
          onViewScores={() => onNavigate("/#scores")}
          onPreviewEdge={() => onNavigate("/#sgp-builder")}
        />
      </main>
      <SiteFooter partnerSlotsEnabled={partnerSlotsEnabled} />
    </div>
  );
}

const LEGAL_CONTENT = {
  terms: {
    eyebrow: "Terms of Use",
    title: "A product preview, not a sportsbook",
    intro: "These terms apply when you use the PricedPlay private product preview.",
    sections: [
      ["What this preview is", "PricedPlay is a fixture-only preview. Its scoreboard uses fictional teams and simulated scores, schedules, markets, and estimates. The Your own slip tool uses only the odds and grouping you enter. PricedPlay has no user accounts, takes no payments, accepts no bets, holds no funds, and does not provide gambling services."],
      ["Pricing estimates", "Fair prices, probabilities, correlation details, and comparisons are informational estimates. They are not guarantees, betting advice, or an inducement to bet. You remain responsible for your own decisions and for following the laws and rules that apply to you."],
      ["Availability and warranty", "The preview is provided as available and without warranties. Results may be incomplete, delayed, or wrong. We may change, limit, suspend, or discontinue any part of the preview at any time."],
      ["Ownership and permitted use", "PricedPlay and its product design, software, branding, and original content are owned by PricedPlay. You may use this private preview for personal evaluation. You may not copy, resell, scrape, republish, reverse engineer, or commercially exploit it without written permission."],
      ["Partner inquiries", "Partner and licensing inquiries should go through the team's existing contacts. No public contact address is offered through this preview."],
    ],
  },
  privacy: {
    eyebrow: "Privacy",
    title: "Minimal data by design",
    intro: "PricedPlay has no accounts, tracking cookies, analytics identifiers, or advertising profiles.",
    sections: [
      ["Your own slip", "When you check your own slip, we keep an anonymous record of the market structure, odds, sportsbook name, and computed prices from your comparison so we can measure how sportsbooks price parlays. Free-text pick labels are not kept."],
      ["Not linked to you", "Slip observations are not linked to any person, account, or device. PricedPlay does not ask for your name, email address, payment details, or sportsbook login, and it does not connect to your sportsbook account."],
      ["Technical operations", "Standard technical server logs are retained briefly for security, reliability, and operational troubleshooting. They are separate from anonymous slip observations."],
      ["Sharing", "We do not sell this data or share it with third parties. There are no advertising or analytics partners."],
      ["Changes", "We will update this notice before introducing any future account, analytics, payment, or additional data-collection feature."],
    ],
  },
  affiliateDisclosure: {
    eyebrow: "Affiliate Disclosure",
    title: "How sponsored links work",
    intro: "PricedPlay may receive compensation when you use a link clearly labeled Sponsored.",
    sections: [
      ["What compensation means", "A partner may pay PricedPlay a fixed fee or commission after a qualifying action. You do not pay PricedPlay for using a sponsored link."],
      ["What does not change", "Compensation can affect which sponsored offers appear or their placement. It does not change the fair price, correlation model, price-cost estimate, or verdict shown for your check."],
      ["Clear labels", "A paid placement is labeled Sponsored before the link. PricedPlay does not name or imply a current partner unless a sponsored slot is configured."],
      ["Your decision", "Check the partner's current terms, availability, eligibility rules, and pricing yourself. A sponsored link is not a recommendation, guarantee, or instruction to bet."],
    ],
  },
};

function LegalPage({ page, partnerSlotsEnabled }) {
  const content = LEGAL_CONTENT[page];
  return (
    <div className="app legal-shell">
      <header className="site-header legal-header">
        <Brand />
        <a href="/">Back to preview</a>
      </header>
      <main className="legal-page">
        <span>{content.eyebrow}</span>
        <h1>{content.title}</h1>
        <p className="legal-intro">{content.intro}</p>
        {content.sections.map(([title, body]) => (
          <section key={title}>
            <h2>{title}</h2>
            <p>{body}</p>
          </section>
        ))}
        <p className="legal-updated">Effective July 22, 2026.</p>
      </main>
      <SiteFooter partnerSlotsEnabled={partnerSlotsEnabled} />
    </div>
  );
}

function HomePage({ partnerSlots, onNavigatePlans }) {
  const [selectedGameId, setSelectedGameId] = useState("sea-por");
  const [selections, setSelections] = useState(initialSelections);
  const [mode, setMode] = useState("own");
  const [ownGames, setOwnGames] = useState([blankOwnGame("own-game-1", "own-leg-1")]);
  const [bookId, setBookId] = useState("");
  const [otherBookName, setOtherBookName] = useState("");
  const [offeredOdds, setOfferedOdds] = useState("+300");
  const [comparisonMessage, setComparisonMessage] = useState("");
  const [hasCompared, setHasCompared] = useState(false);
  const [shareStatus, setShareStatus] = useState("");
  const [pricingState, setPricingState] = useState({ status: "loading", analysis: null });
  const nextOwnGame = useRef(2);
  const nextOwnLeg = useRef(2);
  const selectedGame = GAMES.find((game) => game.id === selectedGameId) ?? GAMES[0];
  const demoGroups = useMemo(() => selectedParlayGroups(selections), [selections]);
  const manualGroups = useMemo(() => ownSlipGroups(ownGames), [ownGames]);
  const selectedBook = useMemo(() => {
    const book = SPORTSBOOKS.find((candidate) => candidate.id === bookId);
    if (!book) return null;
    return {
      id: book.id,
      label: book.id === "other" ? otherBookName.trim() || book.label : book.label,
    };
  }, [bookId, otherBookName]);
  const selectedGroups = mode === "demo" ? demoGroups : manualGroups;
  const selectedLegCount = selectedGroups.reduce((count, group) => count + group.legs.length, 0);
  const ownLegCount = ownGames.reduce((count, game) => count + game.legs.length, 0);
  const ownLegsValid = ownLegCount > 0 && ownLegCount <= 12
    && ownGames.every((game) => game.legs.length > 0
      && game.legs.every((leg) => isValidOwnSlipLeg(game, leg)));
  const legsReady = mode === "demo" ? selectedLegCount > 0 : ownLegsValid;
  const canCompare = legsReady
    && parseAmericanOdds(offeredOdds) !== null
    && (mode === "demo" || selectedBook !== null);
  useEffect(() => {
    if (!legsReady) {
      setPricingState({ status: "idle", analysis: null });
      return undefined;
    }

    const controller = new AbortController();
    const offered = parseAmericanOdds(offeredOdds);
    const payload = {
      legs: selectedGroups.flatMap((group) => group.legs.map((leg) => {
        if (mode === "demo") return pricingLeg(leg, group.game);
        const { label, odds, marginalProbability, ...requestLeg } = leg;
        return requestLeg;
      })),
      ...(offered ? { offeredOdds: offered.odds } : {}),
      ...(mode === "own" && selectedBook ? { book: selectedBook } : {}),
    };
    setPricingState({ status: "loading", analysis: null });

    const loadPrice = async () => {
      try {
        const response = await fetch("/api/parlay/price", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Pricing request failed (${response.status})`);
        const analysis = parlayAnalysis(await response.json());
        if (!analysis) throw new Error("Pricing response was empty");
        setPricingState({ status: "ready", analysis });
      } catch (error) {
        if (error.name === "AbortError") return;
        setPricingState({
          status: "fallback",
          analysis: clientFallbackAnalysis(selectedGroups),
        });
      }
    };

    loadPrice();
    return () => controller.abort();
  }, [legsReady, mode, offeredOdds, selectedBook, selectedGroups]);

  const selectGame = (gameId) => {
    setSelectedGameId(gameId);
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const toggleLeg = (gameId, market, optionId) => {
    setSelections((current) => {
      const legGame = GAMES.find((item) => item.id === gameId);
      const currentIds = current[gameId] ?? [];
      const removing = currentIds.includes(optionId);
      if (removing) return { ...current, [gameId]: currentIds.filter((id) => id !== optionId) };

      const resolvedMarket = market ?? legGame?.markets.find((item) => item.options.some((option) => option.id === optionId));
      const siblingIds = resolvedMarket?.options.map((option) => option.id) ?? [];
      const nextIds = currentIds.filter((id) => !siblingIds.includes(id));
      return { ...current, [gameId]: [...nextIds, optionId].slice(0, 4) };
    });
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const clearLegs = () => {
    if (mode === "demo") {
      setSelections({});
    } else {
      const gameId = `own-game-${nextOwnGame.current++}`;
      setOwnGames([blankOwnGame(gameId, `own-leg-${nextOwnLeg.current++}`)]);
    }
    setHasCompared(false);
    setShareStatus("");
    setComparisonMessage("Parlay cleared.");
  };

  const comparePrice = () => {
    setHasCompared(canCompare);
    setShareStatus("");
    setComparisonMessage(
      canCompare
        ? `Compared ${selectedLegCount} ${selectedLegCount === 1 ? "leg" : "legs"} across ${selectedGroups.length} ${selectedGroups.length === 1 ? "game" : "games"} with the entered price.`
        : "Add at least one leg and valid American odds to compare.",
    );
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const changeOwnGame = (gameId, patch) => {
    setOwnGames((current) => current.map((game) => game.id === gameId ? { ...game, ...patch } : game));
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  // Structured leg edits arrive as patches because one action can set several fields at once — a
  // roster hit sets the player, his id, his team and the lock together.
  const patchOwnLeg = (gameId, legId, patch) => {
    setOwnGames((current) => current.map((game) => game.id === gameId
      ? { ...game, legs: game.legs.map((leg) => leg.id === legId ? { ...leg, ...patch } : leg) }
      : game));
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const addOwnLeg = (gameId) => {
    if (ownLegCount >= 12) return;
    setOwnGames((current) => current.map((game) => game.id === gameId
      ? { ...game, legs: [...game.legs, blankOwnLeg(`own-leg-${nextOwnLeg.current++}`)] }
      : game));
    setHasCompared(false);
    setShareStatus("");
  };

  const removeOwnLeg = (gameId, legId) => {
    setOwnGames((current) => current.map((game) => game.id === gameId
      ? { ...game, legs: game.legs.filter((leg) => leg.id !== legId) }
      : game));
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const addOwnGame = () => {
    if (ownLegCount >= 12) return;
    const gameId = `own-game-${nextOwnGame.current++}`;
    setOwnGames((current) => [...current, blankOwnGame(gameId, `own-leg-${nextOwnLeg.current++}`)]);
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const removeOwnGame = (gameId) => {
    setOwnGames((current) => current.filter((game) => game.id !== gameId));
    setComparisonMessage("");
    setHasCompared(false);
    setShareStatus("");
  };

  const shareCheck = async () => {
    const analysis = pricingState.analysis;
    const offered = parseAmericanOdds(offeredOdds);
    if (!analysis?.correlationCheck?.verdict || !offered) return;
    try {
      await downloadAndShareCard({
        fairOdds: analysis.fairOdds,
        offeredOdds: offered.odds,
        correlationCheck: analysis.correlationCheck,
        legCount: selectedLegCount,
        gameCount: selectedGroups.length,
      });
      setShareStatus("PNG downloaded. The system share sheet also opened when supported.");
    } catch (error) {
      if (error?.name === "AbortError") {
        setShareStatus("PNG downloaded. Sharing was canceled.");
      } else {
        setShareStatus("The share card could not be created.");
      }
    }
  };

  const addGame = () => {
    if (mode === "own") {
      addOwnGame();
      return;
    }
    window.setTimeout(() => scrollToSection("scores"), 120);
  };

  const builderProps = {
    game: selectedGame,
    groups: selectedGroups,
    pricingState,
    selections,
    offeredOdds,
    mode,
    canCompare,
    ownGames,
    bookId,
    otherBookName,
    partnerSlots,
    showShare: hasCompared && Boolean(pricingState.analysis?.correlationCheck?.verdict),
    shareStatus,
    onModeChange: changeMode,
    onBookChange: (value) => {
      setBookId(value);
      setComparisonMessage("");
      setHasCompared(false);
      setShareStatus("");
    },
    onOtherBookNameChange: (value) => {
      setOtherBookName(value);
      setComparisonMessage("");
      setHasCompared(false);
      setShareStatus("");
    },
    onOwnGameChange: changeOwnGame,
    onOwnLegPatch: patchOwnLeg,
    onAddOwnLeg: addOwnLeg,
    onRemoveOwnLeg: removeOwnLeg,
    onRemoveOwnGame: removeOwnGame,
    onOddsChange: (value) => {
      setOfferedOdds(value);
      setComparisonMessage("");
      setHasCompared(false);
      setShareStatus("");
    },
    onToggle: toggleLeg,
    onClear: clearLegs,
    onCompare: comparePrice,
    onShare: shareCheck,
    onAddGame: addGame,
  };

  return (
    <div className="app" id="top">
      <Header
        onNavigatePlans={onNavigatePlans}
      />
      <main>
        <section className="parlay-hero" id="parlay-check" aria-labelledby="parlay-hero-title">
          <div className="hero-copy">
            <span>Parlay Check</span>
            <h1 id="parlay-hero-title">Know what your parlay should really pay.</h1>
            <p>Enter the odds from your sportsbook to see a no-vig fair price, the estimated effect of same-game correlation, and how the offered payout compares.</p>
            <strong>Built from the prices already on your slip. No live feed required.</strong>
          </div>
          <SGPBuilder {...builderProps} />
        </section>
        <p className="comparison-message" aria-live="polite">{comparisonMessage}</p>
        <ScoresWorkspace selectedGameId={selectedGameId} onSelectGame={selectGame} />
        <HowItWorks />
        <aside className="disclaimer" id="demo-boundary">
          <InfoIcon />
          <p><strong>Demo data for product preview.</strong> The scoreboard uses fictional teams and simulated scores, schedules, and markets. Your own slip uses only the odds you enter. No bets or payments are accepted.</p>
        </aside>
      </main>

      <SiteFooter partnerSlotsEnabled={partnerSlots.length > 0} />
    </div>
  );
}

function usePartnerSlots() {
  const [slots, setSlots] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/partner-slots", { signal: controller.signal })
      .then((response) => response.ok ? response.json() : { partnerSlots: [] })
      .then((payload) => {
        const next = Array.isArray(payload?.partnerSlots)
          ? payload.partnerSlots.filter((slot) => (
              typeof slot?.id === "string" && typeof slot?.label === "string"
            ))
          : [];
        setSlots(next);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setSlots([]);
      });
    return () => controller.abort();
  }, []);

  return slots;
}

function App() {
  const partnerSlots = usePartnerSlots();
  const [route, setRoute] = useState(() => routeForPathname(window.location.pathname));
  const partnerSlotsEnabled = partnerSlots.length > 0;

  useEffect(() => {
    const syncRoute = () => setRoute(routeForPathname(window.location.pathname));
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  useEffect(() => {
    const hashTarget = route === "home" ? window.location.hash.slice(1) : "";
    if (hashTarget) {
      window.requestAnimationFrame(() => scrollToSection(hashTarget));
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [route]);

  const navigate = (target) => {
    const next = new URL(target, window.location.origin);
    window.history.pushState(null, "", `${next.pathname}${next.search}${next.hash}`);
    setRoute(routeForPathname(next.pathname));
  };

  if (route === "home") {
    return (
      <HomePage
        partnerSlots={partnerSlots}
        onNavigatePlans={() => navigate("/plans")}
      />
    );
  }
  if (route === "plans") {
    return (
      <PlansPage
        partnerSlotsEnabled={partnerSlotsEnabled}
        onNavigate={navigate}
      />
    );
  }
  return <LegalPage page={route} partnerSlotsEnabled={partnerSlotsEnabled} />;
}

export default App;
